import type { EditorView } from '@tiptap/pm/view';
import type { Node as PMNode } from '@tiptap/pm/model';

import { markSystemTr } from '../plugins/shared';

type AttrsRecord = Record<string, unknown>;

/**
 * Update node attributes at absolute `pos` as a system transaction: out of the undo history, past
 * the template lock, but still saved. For system-driven updates only, never for a user edit.
 *
 * Returns true if a change was dispatched; false if node not found or the patch was a no-op.
 */
const setNodeAttrsNoHistory = <T extends AttrsRecord>(view: EditorView, pos: number, patch: Partial<T>): boolean => {
  const { state, dispatch } = view;

  // ProseMirror's node attrs are not generically typed; narrow once to keep strong typing below.
  const node = state.doc.nodeAt(pos) as (PMNode & { attrs: T }) | null;
  if (!node) return false;

  // Shallow diff only on patched keys to avoid no-op transactions.
  let changed = false;
  for (const key in patch) {
    const k = key as keyof T;
    if (!Object.is(node.attrs[k], patch[k])) {
      changed = true;
      break;
    }
  }
  if (!changed) return false;

  const nextAttrs: T = { ...node.attrs, ...patch };
  dispatch(markSystemTr(state.tr.setNodeMarkup(pos, node.type, nextAttrs, node.marks), { keepAutoSave: true }));
  return true;
};

/** The node-view form: resolves the position itself and does nothing once the node is gone. */
export const setNodeAttrsAt = <T extends AttrsRecord>(
  view: EditorView,
  getPos: () => number | undefined,
  patch: Partial<T>
): boolean => {
  const pos = getPos();
  return pos === undefined ? false : setNodeAttrsNoHistory(view, pos, patch);
};

export default setNodeAttrsNoHistory;
